/**
 * EdgeMail Admin API Service
 * API client for EdgeMail Management Console
 */
import axios from 'axios';
import { getBackendApiBaseUrl } from '@/config/backendApi';
import type {
  DashboardMetrics,
  SystemHealthCheck,
  AdminAlert,
  AdminActivitySummary,
  TenantConfig,
  MailboxStorageInfo,
  AlertSeverity,
  AlertCategory,
  Mailbox,
  MailboxListResponse,
  MailboxFilters,
  CreateMailboxRequest,
  UpdateMailboxRequest,
  KeycloakUser,
  Domain,
  DomainDnsRecords,
  DnsVerificationResult,
  AddDomainRequest,
  QueueHealthStatus,
  OutboundQueueHealthStatus,
  QueueMetrics,
  QueueAlert,
  QueueJobListOptions,
  QueueJobListResult,
  QueueJobDetails,
  QueueType,
  BulkActionResult,
  ConnectedClient,
  SendIdentity,
  CreateSendIdentityRequest,
  SendIdentityStatus,
  EmailEvidenceEmbeddingPolicy,
  UpdateEmailEvidenceEmbeddingPolicyRequest,
  EmailEvidenceEmbeddingSchedule,
  CreateEmailEvidenceEmbeddingScheduleRequest,
  EmailEvidenceEmbeddingRun,
  PreviewEmailEvidenceEmbeddingRunRequest,
} from '../types/admin.types';

const API_BASE = getBackendApiBaseUrl('/api/v1/webemail');

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
api.interceptors.request.use((config) => {
  // Use webelieve_access_token which is the standard token key used by the app
  const token = localStorage.getItem('webelieve_access_token');
  console.log('[EdgeMailAdminAPI] Interceptor - token found:', !!token, 'for URL:', config.url);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('[EdgeMailAdminAPI] Added Authorization header');
  } else {
    console.warn('[EdgeMailAdminAPI] No token in localStorage! Keys:', Object.keys(localStorage));
  }
  return config;
});

// Export the api instance for use by other hooks
export { api as edgeMailApi };

interface WeBeMailHealthResponse {
  product: 'webemail';
  status: 'healthy' | 'not_configured' | 'unhealthy';
  storage: string;
  counts?: {
    tenants?: number;
    mailboxes?: number;
    folders?: number;
  };
  capabilities?: Record<string, boolean>;
  timestamp: string;
}

interface WeBeMailQueueHealthResponse {
  product: 'webemail';
  redis: {
    status: string;
    ready: boolean;
  };
  workerFoundation: true;
  workersRunning: boolean;
  queues: Array<{
    key: 'inbound' | 'outbound' | 'maintenance';
    name: string;
    counts: {
      waiting: number;
      active: number;
      delayed: number;
      failed: number;
      completed: number;
      paused: number;
    };
    worker: {
      running: boolean;
      status: 'ready' | 'stale' | 'stopped' | 'unknown';
      pid?: number;
      host?: string;
      updatedAt?: string;
    };
  }>;
  timestamp: string;
}

interface WeBeMailDashboardMetricsResponse {
  metrics: DashboardMetrics;
}

interface WeBeMailMailboxSummary {
  id: string;
  email: string;
  displayName: string | null;
  tenantId: string;
  realmId: string;
  quotaBytes: number;
  usedBytes: number;
  messageCount: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

interface WeBeMailDomainSummary {
  id: string;
  tenantId: string;
  domain: string;
  status: string;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

function toDomainStatus(status: string | undefined): Domain['status'] {
  if (status === 'verified' || status === 'failed' || status === 'pending') {
    return status;
  }
  return 'pending';
}

function mapWeBeMailMailbox(raw: WeBeMailMailboxSummary): Mailbox {
  const domainName = raw.email.includes('@') ? raw.email.split('@')[1] : '';
  return {
    mailbox_id: raw.id,
    tenant_id: raw.tenantId,
    domain_id: domainName,
    keycloak_user_id: '',
    email: raw.email,
    local_part: raw.email.split('@')[0] || raw.email,
    display_name: raw.displayName,
    quota_bytes: raw.quotaBytes,
    used_bytes: raw.usedBytes,
    message_count: raw.messageCount,
    auto_reply_enabled: false,
    auto_reply_subject: null,
    auto_reply_body: null,
    auto_reply_start: null,
    auto_reply_end: null,
    forward_to: null,
    forward_keep_copy: false,
    signature_html: null,
    signature_plain: null,
    is_active: raw.isActive,
    last_login_at: null,
    created_at: raw.createdAt || '',
    updated_at: raw.updatedAt || '',
    domain: domainName
      ? {
          domain_id: domainName,
          domain: domainName,
          status: 'verified',
        }
      : undefined,
  };
}

function mapWeBeMailDomain(raw: WeBeMailDomainSummary): Domain {
  const status = toDomainStatus(raw.status);
  const verified = status === 'verified';
  return {
    domain_id: raw.id,
    tenant_id: raw.tenantId,
    domain: raw.domain,
    status,
    domain_type: raw.isPrimary ? 'system' : 'custom',
    verification_token: '',
    verification_method: 'mx',
    verified_at: verified ? raw.updatedAt : null,
    mx_verified: verified,
    spf_verified: verified,
    dkim_verified: verified,
    dmarc_verified: verified,
    dkim_selector: '',
    dkim_public_key: '',
    catch_all_mailbox_id: null,
    is_primary: raw.isPrimary,
    is_active: raw.isActive,
    created_at: raw.createdAt || '',
    updated_at: raw.updatedAt || '',
  };
}

function mapQueueHealth(
  queueHealth: WeBeMailQueueHealthResponse,
  key: 'inbound' | 'outbound',
): QueueHealthStatus | OutboundQueueHealthStatus {
  const queue = queueHealth.queues.find((candidate) => candidate.key === key);
  const counts = queue?.counts || {
    waiting: 0,
    active: 0,
    delayed: 0,
    failed: 0,
    completed: 0,
    paused: 0,
  };
  const workerRunning = Boolean(queue?.worker.running);
  const alerts: QueueAlert[] = [];

  if (!queueHealth.redis.ready) {
    alerts.push({
      level: 'critical',
      code: 'redis_unavailable',
      message: 'Redis is not ready.',
    });
  }
  if (!workerRunning) {
    alerts.push({
      level: queue?.worker.status === 'stale' ? 'warning' : 'critical',
      code: 'worker_not_ready',
      message: `${key === 'inbound' ? 'Inbound' : 'Outbound'} worker is ${queue?.worker.status || 'unknown'}.`,
    });
  }

  const common = {
    connected: queueHealth.redis.ready,
    dlq: { waiting: 0 },
    worker: {
      running: workerRunning,
      concurrency: workerRunning ? 1 : 0,
    },
    alerts,
  };

  if (key === 'inbound') {
    return {
      ...common,
      inbound: counts,
    } as QueueHealthStatus;
  }

  return {
    ...common,
    outbound: counts,
  } as OutboundQueueHealthStatus;
}

async function getWeBeMailHealth(): Promise<WeBeMailHealthResponse> {
  const response = await api.get<WeBeMailHealthResponse>('/health');
  return response.data;
}

async function getWeBeMailQueueHealth(): Promise<WeBeMailQueueHealthResponse> {
  const response = await api.get<{ health: WeBeMailQueueHealthResponse }>('/admin/queues/health');
  return response.data.health;
}

async function getWeBeMailMailboxes(): Promise<Mailbox[]> {
  const response = await api.get<{ mailboxes: WeBeMailMailboxSummary[] }>('/admin/mailboxes');
  return response.data.mailboxes.map(mapWeBeMailMailbox);
}

async function getWeBeMailDomains(): Promise<Domain[]> {
  const response = await api.get<{ domains: WeBeMailDomainSummary[] }>('/admin/domains');
  return response.data.domains.map(mapWeBeMailDomain);
}

/**
 * EdgeMail Admin API
 */
export const EdgeMailAdminAPI = {
  // ============ Dashboard ============

  /**
   * Get dashboard metrics
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await api.get<WeBeMailDashboardMetricsResponse>('/admin/dashboard/metrics');
    return response.data.metrics;
  },

  /**
   * Get system health status
   */
  async getSystemHealth(): Promise<SystemHealthCheck> {
    const [health, queueHealth] = await Promise.all([
      getWeBeMailHealth(),
      getWeBeMailQueueHealth(),
    ]);
    const lastChecked = health.timestamp || queueHealth.timestamp || new Date().toISOString();
    const databaseStatus: SystemHealthCheck['components']['database']['status'] =
      health.status === 'healthy' ? 'operational' : health.status === 'not_configured' ? 'degraded' : 'down';
    const queueStatus: SystemHealthCheck['components']['smtp']['status'] =
      queueHealth.redis.ready && queueHealth.workersRunning ? 'operational' : queueHealth.redis.ready ? 'degraded' : 'down';

    return {
      overall: health.status === 'healthy' && queueStatus === 'operational' ? 'healthy' : health.status === 'unhealthy' ? 'critical' : 'degraded',
      components: {
        database: {
          status: databaseStatus,
          message: health.status === 'healthy' ? undefined : `WeBeMail storage is ${health.status}.`,
          lastChecked,
        },
        smtp: {
          status: queueStatus,
          message: queueStatus === 'operational' ? undefined : 'SMTP queue worker foundation is not fully ready.',
          lastChecked,
        },
        imap: {
          status: health.capabilities?.imapAccess ? 'operational' : 'degraded',
          message: health.capabilities?.imapAccess ? undefined : 'IMAP mailbox access is not enabled.',
          lastChecked,
        },
        storage: {
          status: health.capabilities?.rawMimeStorage ? 'operational' : 'degraded',
          message: health.capabilities?.rawMimeStorage ? undefined : 'Raw MIME storage is not enabled.',
          lastChecked,
        },
        keycloak: {
          status: 'operational',
          lastChecked,
        },
      },
      lastChecked,
    };
  },

  /**
   * Get active alerts
   */
  async getAlerts(options?: {
    severity?: AlertSeverity;
    category?: AlertCategory;
    includeResolved?: boolean;
    limit?: number;
  }): Promise<AdminAlert[]> {
    const queueHealth = await getWeBeMailQueueHealth();
    const alerts: AdminAlert[] = [];
    if (!queueHealth.redis.ready) {
      alerts.push({
        alertId: 'webemail-redis-not-ready',
        tenantId: 'webemail',
        category: 'system',
        severity: 'critical',
        title: 'Redis is not ready',
        description: 'The WeBeMail queue foundation cannot report healthy until Redis is ready.',
        isAcknowledged: false,
        isResolved: false,
        createdAt: queueHealth.timestamp,
      });
    }
    if (!queueHealth.workersRunning) {
      alerts.push({
        alertId: 'webemail-workers-not-ready',
        tenantId: 'webemail',
        category: 'delivery',
        severity: 'warning',
        title: 'WeBeMail workers are not all ready',
        description: 'At least one bounded WeBeMail worker heartbeat is stale, stopped, or unknown.',
        isAcknowledged: false,
        isResolved: false,
        createdAt: queueHealth.timestamp,
      });
    }

    return alerts
      .filter((alert) => !options?.severity || alert.severity === options.severity)
      .filter((alert) => !options?.category || alert.category === options.category)
      .slice(0, options?.limit || alerts.length);
  },

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(alertId: string): Promise<void> {
    void alertId;
  },

  /**
   * Get admin activity summary
   */
  async getAdminActivitySummary(options?: {
    days?: number;
    limit?: number;
  }): Promise<AdminActivitySummary> {
    void options;
    return {
      totalActions: 0,
      mailboxActions: 0,
      domainActions: 0,
      securityActions: 0,
      complianceActions: 0,
      recentActions: [],
    };
  },

  /**
   * Get top storage users
   */
  async getTopStorageUsers(limit?: number): Promise<MailboxStorageInfo[]> {
    const mailboxes = await getWeBeMailMailboxes();
    return mailboxes
      .map((mailbox) => ({
        mailboxId: mailbox.mailbox_id,
        email: mailbox.email,
        displayName: mailbox.display_name || mailbox.email,
        usedBytes: mailbox.used_bytes || 0,
        quotaBytes: mailbox.quota_bytes || 0,
        percentUsed: mailbox.quota_bytes > 0 ? ((mailbox.used_bytes || 0) / mailbox.quota_bytes) * 100 : 0,
      }))
      .sort((a, b) => b.usedBytes - a.usedBytes)
      .slice(0, limit || 5);
  },

  // ============ Tenant Configuration ============

  /**
   * Get tenant configuration
   */
  async getTenantConfig(): Promise<TenantConfig> {
    const [health, domains] = await Promise.all([
      getWeBeMailHealth(),
      getWeBeMailDomains(),
    ]);
    const tenantId = domains[0]?.tenant_id || 'webemail';

    return {
      tenant: {
        tenant_id: tenantId,
        name: 'WeBeMail',
        slug: 'webemail',
        keycloak_realm: 'edgelex-llc',
        max_mailboxes: 100,
        max_storage_bytes: 100 * 1024 * 1024 * 1024,
        settings: {},
        compliance_settings: {
          aba_ethics_enabled: false,
          soc2_logging_enabled: true,
          gdpr_enabled: false,
          retention_days: 365,
        },
        is_active: health.status === 'healthy',
        created_at: health.timestamp,
        updated_at: health.timestamp,
      },
      limits: {
        max_mailboxes: 100,
        max_domains: 10,
        max_storage_gb: 100,
        max_message_size_mb: 25,
        retention_days: 365,
        features: {
          custom_domains: true,
          email_aliases: false,
          auto_reply: true,
          email_forwarding: true,
          spam_filtering: false,
          virus_scanning: false,
          dkim_signing: true,
          audit_logging: true,
          legal_hold: false,
          compliance_reporting: false,
          api_access: false,
          send_identities: false,
        },
      },
      features: health.capabilities || {},
    };
  },

  /**
   * Update tenant configuration
   */
  async updateTenantConfig(config: Record<string, unknown>): Promise<void> {
    void config;
    throw new Error('Tenant configuration mutation is not enabled in the bounded WeBeMail admin adapter.');
  },

  // ============ Admin Actions ============

  /**
   * Sync users from Keycloak
   */
  async syncKeycloakUsers(): Promise<{
    synced: number;
    created: number;
    errors: string[];
  }> {
    const response = await api.post<{
      synced: number;
      created: number;
      errors: string[];
    }>('/actions/sync-keycloak');
    return response.data;
  },

  /**
   * Clear tenant cache
   */
  async clearCache(): Promise<void> {
    await api.post('/actions/clear-cache');
  },

  // ============ Mailbox Management (Phase 2) ============

  /**
   * Get paginated list of mailboxes
   */
  async getMailboxes(filters?: MailboxFilters): Promise<MailboxListResponse> {
    let mailboxes = await getWeBeMailMailboxes();

    if (filters?.search) {
      const query = filters.search.toLowerCase();
      mailboxes = mailboxes.filter((mailbox) =>
        mailbox.email.toLowerCase().includes(query)
        || (mailbox.display_name || '').toLowerCase().includes(query),
      );
    }
    if (filters?.status === 'active') {
      mailboxes = mailboxes.filter((mailbox) => mailbox.is_active);
    }
    if (filters?.status === 'suspended') {
      mailboxes = mailboxes.filter((mailbox) => !mailbox.is_active);
    }
    if (filters?.domainId) {
      mailboxes = mailboxes.filter((mailbox) => mailbox.domain_id === filters.domainId || mailbox.domain?.domain_id === filters.domainId);
    }

    if (filters?.sortBy) {
      const direction = filters.sortOrder === 'desc' ? -1 : 1;
      mailboxes = [...mailboxes].sort((a, b) => {
        const left = a[filters.sortBy!] ?? '';
        const right = b[filters.sortBy!] ?? '';
        if (typeof left === 'number' && typeof right === 'number') {
          return (left - right) * direction;
        }
        return String(left).localeCompare(String(right)) * direction;
      });
    }

    const pageSize = filters?.pageSize || 20;
    const page = filters?.page || 1;
    const total = mailboxes.length;
    const start = (page - 1) * pageSize;

    return {
      mailboxes: mailboxes.slice(start, start + pageSize),
      total,
      page,
      pageSize,
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
    };
  },

  /**
   * Get a single mailbox by ID
   */
  async getMailbox(mailboxId: string): Promise<Mailbox> {
    const mailboxes = await getWeBeMailMailboxes();
    const mailbox = mailboxes.find((candidate) => candidate.mailbox_id === mailboxId);
    if (!mailbox) {
      throw new Error('Mailbox not found');
    }
    return mailbox;
  },

  /**
   * Create a new mailbox
   */
  async createMailbox(data: CreateMailboxRequest): Promise<Mailbox> {
    const response = await api.post<{ mailbox: Mailbox }>('/mailboxes', data);
    return response.data.mailbox;
  },

  /**
   * Update a mailbox
   */
  async updateMailbox(mailboxId: string, data: UpdateMailboxRequest): Promise<Mailbox> {
    const response = await api.patch<{ mailbox: Mailbox }>(`/mailboxes/${mailboxId}`, data);
    return response.data.mailbox;
  },

  /**
   * Suspend a mailbox
   */
  async suspendMailbox(mailboxId: string): Promise<void> {
    await api.post(`/mailboxes/${mailboxId}/suspend`);
  },

  /**
   * Reactivate a mailbox
   */
  async reactivateMailbox(mailboxId: string): Promise<void> {
    await api.post(`/mailboxes/${mailboxId}/reactivate`);
  },

  /**
   * Delete a mailbox
   */
  async deleteMailbox(mailboxId: string): Promise<void> {
    await api.delete(`/mailboxes/${mailboxId}`);
  },

  /**
   * Update mailbox quota
   */
  async updateMailboxQuota(mailboxId: string, quotaBytes: number): Promise<Mailbox> {
    const response = await api.patch<{ mailbox: Mailbox }>(`/mailboxes/${mailboxId}/quota`, {
      quota_bytes: quotaBytes,
    });
    return response.data.mailbox;
  },

  async listSendIdentities(mailboxId: string): Promise<SendIdentity[]> {
    const response = await api.get<{ identities: SendIdentity[] }>(`/mailboxes/${mailboxId}/send-identities`);
    return response.data.identities;
  },

  async createSendIdentity(mailboxId: string, data: CreateSendIdentityRequest): Promise<SendIdentity> {
    const response = await api.post<{ identity: SendIdentity }>(`/mailboxes/${mailboxId}/send-identities`, data);
    return response.data.identity;
  },

  async verifySendIdentity(mailboxId: string, identityId: string): Promise<SendIdentity> {
    const response = await api.post<{ identity: SendIdentity }>(`/mailboxes/${mailboxId}/send-identities/${identityId}/verify`);
    return response.data.identity;
  },

  async updateSendIdentity(
    mailboxId: string,
    identityId: string,
    data: {
      isDefault?: boolean;
      status?: SendIdentityStatus;
      replyMatchingEnabled?: boolean;
      displayName?: string | null;
    },
  ): Promise<SendIdentity> {
    const response = await api.patch<{ identity: SendIdentity }>(`/mailboxes/${mailboxId}/send-identities/${identityId}`, data);
    return response.data.identity;
  },

  async deleteSendIdentity(mailboxId: string, identityId: string): Promise<void> {
    await api.delete(`/mailboxes/${mailboxId}/send-identities/${identityId}`);
  },

  // ============ Domain Management ============

  /**
   * Get list of domains for the tenant
   */
  async getDomains(): Promise<Domain[]> {
    return getWeBeMailDomains();
  },

  async getDomain(domainId: string): Promise<{ domain: Domain; dnsRecords: DomainDnsRecords }> {
    const domains = await getWeBeMailDomains();
    const domain = domains.find((candidate) => candidate.domain_id === domainId);
    if (!domain) {
      throw new Error('Domain not found');
    }
    return {
      domain,
      dnsRecords: {
        mx: { priority: 10, host: `mail.${domain.domain}` },
        spf: `v=spf1 include:spf.brevo.com mx ~all`,
        dkim_selector: domain.dkim_selector || 'brevo1',
        dkim_public_key: domain.dkim_public_key || '',
        dmarc: 'v=DMARC1; p=none; rua=mailto:rua@dmarc.brevo.com',
        verification_txt: domain.verification_token || '',
      },
    };
  },

  async addDomain(tenantId: string, data: AddDomainRequest): Promise<{ domain: Domain; dnsRecords: DomainDnsRecords }> {
    void tenantId;
    const response = await api.post<{ domain: WeBeMailDomainSummary }>('/admin/domains', {
      domain: data.domain,
      isPrimary: data.is_primary,
    });
    const domain = mapWeBeMailDomain(response.data.domain);
    const details = await this.getDomain(domain.domain_id);
    return { ...details, domain };
  },

  async verifyDomain(domainId: string): Promise<{ verification_result: DnsVerificationResult; is_verified: boolean }> {
    const { domain } = await this.getDomain(domainId);
    const isVerified = domain.status === 'verified';
    return {
      is_verified: isVerified,
      verification_result: {
        domain_verified: isVerified,
        mx_valid: domain.mx_verified,
        spf_valid: domain.spf_verified,
        dkim_valid: domain.dkim_verified,
        dmarc_valid: domain.dmarc_verified,
        errors: isVerified ? [] : ['Domain verification is not complete in the bounded WeBeMail admin adapter.'],
      },
    };
  },

  async setPrimaryDomain(domainId: string): Promise<void> {
    void domainId;
    throw new Error('Primary-domain mutation is not enabled in the bounded WeBeMail admin adapter.');
  },

  async deleteDomain(domainId: string): Promise<void> {
    void domainId;
    throw new Error('Domain deletion is not enabled in the bounded WeBeMail admin adapter.');
  },

  async updateCatchAll(domainId: string, enabled: boolean, targetMailboxId?: string): Promise<void> {
    await api.patch(`/domains/${domainId}/catch-all`, { enabled, target_mailbox_id: targetMailboxId });
  },

  async regenerateDkim(domainId: string): Promise<{ new_selector: string; dns_record: string; message: string }> {
    void domainId;
    throw new Error('DKIM regeneration is not enabled in the bounded WeBeMail admin adapter.');
  },

  // ============ Connected Clients ============

  /**
   * Get connected clients for the tenant
   */
  async getConnectedClients(): Promise<ConnectedClient[]> {
    return [];
  },

  // ============ Evidence Indexing Governance ============

  async getEvidenceEmbeddingPolicy(mailboxId?: string): Promise<EmailEvidenceEmbeddingPolicy> {
    const params = mailboxId ? `?mailboxId=${encodeURIComponent(mailboxId)}` : '';
    const response = await api.get<{ policy: EmailEvidenceEmbeddingPolicy }>(
      `/evidence-embeddings/policy${params}`,
    );
    return response.data.policy;
  },

  async updateEvidenceEmbeddingPolicy(
    input: UpdateEmailEvidenceEmbeddingPolicyRequest,
  ): Promise<EmailEvidenceEmbeddingPolicy> {
    const response = await api.put<{ policy: EmailEvidenceEmbeddingPolicy }>(
      '/evidence-embeddings/policy',
      input,
    );
    return response.data.policy;
  },

  async listEvidenceEmbeddingSchedules(): Promise<EmailEvidenceEmbeddingSchedule[]> {
    const response = await api.get<{ schedules: EmailEvidenceEmbeddingSchedule[] }>(
      '/evidence-embeddings/schedules',
    );
    return response.data.schedules;
  },

  async createEvidenceEmbeddingSchedule(
    input: CreateEmailEvidenceEmbeddingScheduleRequest,
  ): Promise<EmailEvidenceEmbeddingSchedule> {
    const response = await api.post<{ schedule: EmailEvidenceEmbeddingSchedule }>(
      '/evidence-embeddings/schedules',
      input,
    );
    return response.data.schedule;
  },

  async cancelEvidenceEmbeddingSchedule(scheduleId: string): Promise<EmailEvidenceEmbeddingSchedule> {
    const response = await api.post<{ schedule: EmailEvidenceEmbeddingSchedule }>(
      `/evidence-embeddings/schedules/${scheduleId}/cancel`,
    );
    return response.data.schedule;
  },

  async previewEvidenceEmbeddingRun(
    input: PreviewEmailEvidenceEmbeddingRunRequest,
  ): Promise<EmailEvidenceEmbeddingRun> {
    const response = await api.post<{ run: EmailEvidenceEmbeddingRun }>(
      '/evidence-embeddings/preview',
      input,
    );
    return response.data.run;
  },

  async listEvidenceEmbeddingRuns(limit = 50): Promise<EmailEvidenceEmbeddingRun[]> {
    const response = await api.get<{ runs: EmailEvidenceEmbeddingRun[] }>(
      `/evidence-embeddings/runs?limit=${limit}`,
    );
    return response.data.runs;
  },

  async startEvidenceEmbeddingRun(runId: string): Promise<EmailEvidenceEmbeddingRun> {
    const response = await api.post<{ run: EmailEvidenceEmbeddingRun }>(
      `/evidence-embeddings/runs/${runId}/start`,
      { previewRunId: runId },
    );
    return response.data.run;
  },

  async cancelEvidenceEmbeddingRun(runId: string): Promise<EmailEvidenceEmbeddingRun> {
    const response = await api.post<{ run: EmailEvidenceEmbeddingRun }>(
      `/evidence-embeddings/runs/${runId}/cancel`,
    );
    return response.data.run;
  },

  // ============ Keycloak Users ============

  /**
   * Get Keycloak users that can be linked to mailboxes
   */
  async getKeycloakUsers(options?: {
    search?: string;
    withoutMailbox?: boolean;
  }): Promise<KeycloakUser[]> {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.withoutMailbox) params.append('excludeWithMailbox', 'true');

    const response = await api.get<{ users: KeycloakUser[] }>(`/admin/keycloak/users?${params}`);
    return response.data.users;
  },

  // ============ Queue Health (Phase 4.4) ============

  /**
   * Get inbound queue health
   */
  async getInboundQueueHealth(): Promise<{
    health: QueueHealthStatus;
    metrics: QueueMetrics;
  }> {
    const queueHealth = await getWeBeMailQueueHealth();
    return {
      health: mapQueueHealth(queueHealth, 'inbound') as QueueHealthStatus,
      metrics: {},
    };
  },

  /**
   * Get outbound queue health
   */
  async getOutboundQueueHealth(): Promise<{
    health: OutboundQueueHealthStatus;
  }> {
    const queueHealth = await getWeBeMailQueueHealth();
    return {
      health: mapQueueHealth(queueHealth, 'outbound') as OutboundQueueHealthStatus,
    };
  },

  /**
   * Get outbound queue metrics
   */
  async getOutboundQueueMetrics(domain?: string): Promise<{
    metrics: QueueMetrics;
  }> {
    void domain;
    return { metrics: {} };
  },

  // ============================================================================
  // QUEUE JOB MANAGEMENT (Exchange-style)
  // ============================================================================

  /**
   * Get queue jobs with filtering, sorting, and pagination
   */
  async getQueueJobs(options: Partial<QueueJobListOptions>): Promise<QueueJobListResult> {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.queueType) params.append('queueType', options.queueType);
    if (options.page) params.append('page', String(options.page));
    if (options.pageSize) params.append('pageSize', String(options.pageSize));
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.filterDomain) params.append('filterDomain', options.filterDomain);
    if (options.filterFrom) params.append('filterFrom', options.filterFrom);
    if (options.filterTo) params.append('filterTo', options.filterTo);
    if (options.filterSubject) params.append('filterSubject', options.filterSubject);

    const response = await api.get(`/queue/jobs?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single job details
   */
  async getJobDetails(jobId: string, queueType: QueueType, isDlq = false): Promise<{ job: QueueJobDetails }> {
    const params = new URLSearchParams({ queueType, isDlq: String(isDlq) });
    const response = await api.get(`/queue/jobs/${jobId}?${params.toString()}`);
    return response.data;
  },

  /**
   * Remove a job from the queue
   */
  async removeJob(jobId: string, queueType: QueueType, isDlq = false): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams({ queueType, isDlq: String(isDlq) });
    const response = await api.delete(`/queue/jobs/${jobId}?${params.toString()}`);
    return response.data;
  },

  /**
   * Retry a failed job
   */
  async retryJob(jobId: string, queueType: QueueType): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams({ queueType });
    const response = await api.post(`/queue/jobs/${jobId}/retry?${params.toString()}`);
    return response.data;
  },

  /**
   * Replay a DLQ job (moves it back to main queue)
   */
  async replayJob(jobId: string, queueType: QueueType): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams({ queueType });
    const response = await api.post(`/queue/jobs/${jobId}/replay?${params.toString()}`);
    return response.data;
  },

  /**
   * Bulk remove jobs
   */
  async bulkRemoveJobs(jobIds: string[], queueType: QueueType, isDlq = false): Promise<BulkActionResult> {
    const response = await api.post('/queue/jobs/bulk/remove', { jobIds, queueType, isDlq });
    return response.data;
  },

  /**
   * Bulk replay DLQ jobs
   */
  async bulkReplayJobs(jobIds: string[], queueType: QueueType): Promise<BulkActionResult> {
    const response = await api.post('/queue/jobs/bulk/replay', { jobIds, queueType });
    return response.data;
  },

  /**
   * Export queue jobs to CSV (returns download URL)
   */
  getExportUrl(options: Partial<QueueJobListOptions>): string {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.queueType) params.append('queueType', options.queueType);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.filterDomain) params.append('filterDomain', options.filterDomain);
    if (options.filterFrom) params.append('filterFrom', options.filterFrom);
    if (options.filterTo) params.append('filterTo', options.filterTo);
    if (options.filterSubject) params.append('filterSubject', options.filterSubject);

    return `${API_BASE}/queue/jobs/export?${params.toString()}`;
  },

  // ==========================================================================
  // BLOCKED SENDERS (Report Phase B)
  // ==========================================================================

  async listBlockedSenders(mailboxId?: string): Promise<BlockedSender[]> {
    const params = mailboxId ? { mailbox_id: mailboxId } : undefined;
    const { data } = await api.get<{ blocked_senders: BlockedSender[] }>(
      `/blocked-senders`,
      { params },
    );
    return data.blocked_senders;
  },

  async createBlockedSender(input: CreateBlockedSenderInput): Promise<BlockedSender> {
    const { data } = await api.post<{ blocked_sender: BlockedSender }>(
      `/blocked-senders`,
      input,
    );
    return data.blocked_sender;
  },

  async deleteBlockedSender(blockedSenderId: string): Promise<void> {
    await api.delete(`/blocked-senders/${blockedSenderId}`);
  },

  // ==========================================================================
  // REJECTED MAIL (persisted, realm-scoped, fully manageable)
  // ==========================================================================

  _buildRejectedMailQuery(params: ListRejectedMailParams): string {
    const search = new URLSearchParams();
    if (params.actions && params.actions.length > 0) {
      for (const a of params.actions) search.append('actions', a);
    }
    if (params.sender) search.append('sender', params.sender);
    if (params.recipient) search.append('recipient', params.recipient);
    if (params.q) search.append('q', params.q);
    if (params.since !== undefined) search.append('since', String(params.since));
    if (params.until !== undefined) search.append('until', String(params.until));
    if (params.limit !== undefined) search.append('limit', String(params.limit));
    return search.toString();
  },

  async listRejectedMail(params: ListRejectedMailParams = {}): Promise<RejectedMailRow[]> {
    const qs = this._buildRejectedMailQuery(params);
    const { data } = await api.get<{ rejected_mail: RejectedMailRow[] }>(
      `/rejected-mail${qs ? `?${qs}` : ''}`,
    );
    return data.rejected_mail;
  },

  async getRejectedMailDetail(rejectionId: string): Promise<RejectedMailDetail> {
    const { data } = await api.get<{ rejected_mail: RejectedMailDetail }>(
      `/rejected-mail/${rejectionId}`,
    );
    return data.rejected_mail;
  },

  async releaseRejection(rejectionId: string): Promise<ReleaseRejectionResult> {
    const { data, status } = await api.post<ReleaseRejectionResult>(
      `/rejected-mail/${rejectionId}/release`,
      {},
      { validateStatus: (s) => s === 200 || s === 409 },
    );
    return { ...data, statusHttp: status };
  },

  async bulkReleaseRejections(rejectionIds: string[]): Promise<ReleaseRejectionResult[]> {
    const { data } = await api.post<{ results: ReleaseRejectionResult[] }>(
      `/rejected-mail/release-bulk`,
      { rejection_ids: rejectionIds },
    );
    return data.results;
  },

  async whitelistFromRejection(rejectionId: string): Promise<{ allowedSenderId: string }> {
    const { data } = await api.post<{ allowedSenderId: string }>(
      `/rejected-mail/${rejectionId}/whitelist`,
      {},
    );
    return data;
  },

  async bulkBlockFromRejections(
    rejectionIds: string[],
  ): Promise<Array<{ rejectionId: string; ok: boolean; reason?: string }>> {
    const { data } = await api.post<{
      results: Array<{ rejectionId: string; ok: boolean; reason?: string }>;
    }>(`/rejected-mail/block-bulk`, { rejection_ids: rejectionIds });
    return data.results;
  },

  /** Returns a download URL for the CSV export of the current filter view. */
  getRejectedMailExportUrl(params: ListRejectedMailParams = {}): string {
    const qs = this._buildRejectedMailQuery(params);
    return `${API_BASE}/rejected-mail/export${qs ? `?${qs}` : ''}`;
  },

  // ==========================================================================
  // ALLOWED SENDERS (per-mailbox)
  // ==========================================================================

  async listAllowedSenders(mailboxId?: string): Promise<AllowedSender[]> {
    const params = mailboxId ? { mailbox_id: mailboxId } : undefined;
    const { data } = await api.get<{ allowed_senders: AllowedSender[] }>(
      `/allowed-senders`,
      { params },
    );
    return data.allowed_senders;
  },

  async createAllowedSender(input: CreateAllowedSenderInput): Promise<AllowedSender> {
    const { data } = await api.post<{ allowed_sender: AllowedSender }>(
      `/allowed-senders`,
      input,
    );
    return data.allowed_sender;
  },

  async deleteAllowedSender(allowedSenderId: string): Promise<void> {
    await api.delete(`/allowed-senders/${allowedSenderId}`);
  },

  // ==========================================================================
  // MIGRATION (Phase 1 PR 5 — wraps PR 4 controller surface)
  // ==========================================================================

  async listMigrationJobs(opts?: import('../types/migration.types').ListMigrationJobsOptions): Promise<import('../types/migration.types').ListMigrationJobsResponse> {
    const params = new URLSearchParams();
    if (opts?.status) params.append('status', opts.status);
    if (opts?.mailbox_id) params.append('mailbox_id', opts.mailbox_id);
    if (opts?.limit !== undefined) params.append('limit', String(opts.limit));
    if (opts?.offset !== undefined) params.append('offset', String(opts.offset));
    const qs = params.toString();
    const { data } = await api.get<import('../types/migration.types').ListMigrationJobsResponse>(
      `/migration/jobs${qs ? `?${qs}` : ''}`,
    );
    return data;
  },

  async getMigrationJob(jobId: string): Promise<import('../types/migration.types').MigrationJobDetailResponse> {
    const { data } = await api.get<import('../types/migration.types').MigrationJobDetailResponse>(
      `/migration/jobs/${jobId}`,
    );
    return data;
  },

  async createMigrationJob(input: import('../types/migration.types').CreateMigrationJobInput): Promise<import('../types/migration.types').MigrationJob> {
    const { data } = await api.post<{ job: import('../types/migration.types').MigrationJob }>(
      `/migration/jobs`,
      input,
    );
    return data.job;
  },

  async cancelMigrationJob(jobId: string): Promise<import('../types/migration.types').MigrationJob> {
    const { data } = await api.post<{ job: import('../types/migration.types').MigrationJob }>(
      `/migration/jobs/${jobId}/cancel`,
    );
    return data.job;
  },

  /**
   * Pre-flight Validator — backend ships PR 11; client method exists now so
   * the wizard can wire its onClick early and gracefully render the 501.
   */
  async validateMigrationJob(jobId: string): Promise<import('../types/migration.types').ValidationReport> {
    const { data } = await api.post<{ validation_report: import('../types/migration.types').ValidationReport }>(
      `/migration/jobs/${jobId}/validate`,
    );
    return data.validation_report;
  },

  async pauseMigrationJob(jobId: string): Promise<import('../types/migration.types').MigrationJob> {
    const { data } = await api.post<{ job: import('../types/migration.types').MigrationJob }>(
      `/migration/jobs/${jobId}/pause`,
    );
    return data.job;
  },

  async resumeMigrationJob(jobId: string): Promise<import('../types/migration.types').MigrationJob> {
    const { data } = await api.post<{ job: import('../types/migration.types').MigrationJob }>(
      `/migration/jobs/${jobId}/resume`,
    );
    return data.job;
  },

  async promoteMigrationCanary(jobId: string): Promise<import('../types/migration.types').MigrationJob> {
    const { data } = await api.post<{ job: import('../types/migration.types').MigrationJob }>(
      `/migration/jobs/${jobId}/promote-canary`,
    );
    return data.job;
  },

  async listMigrationDlq(jobId: string, opts?: { error_code?: string; error_class?: string; suppressed?: boolean; limit?: number; offset?: number }): Promise<import('../types/migration.types').ListMigrationDlqResponse> {
    const params = new URLSearchParams();
    if (opts?.error_code) params.append('error_code', opts.error_code);
    if (opts?.error_class) params.append('error_class', opts.error_class);
    if (opts?.suppressed !== undefined) params.append('suppressed', String(opts.suppressed));
    if (opts?.limit !== undefined) params.append('limit', String(opts.limit));
    if (opts?.offset !== undefined) params.append('offset', String(opts.offset));
    const qs = params.toString();
    const { data } = await api.get<import('../types/migration.types').ListMigrationDlqResponse>(
      `/migration/jobs/${jobId}/dlq${qs ? `?${qs}` : ''}`,
    );
    return data;
  },

  async replayMigrationDlqEntry(jobId: string, dlqId: string): Promise<void> {
    await api.post(`/migration/jobs/${jobId}/dlq/${dlqId}/replay`);
  },

  async replayMigrationDlqBulk(
    jobId: string,
    filter: { error_code?: string; error_class?: string },
  ): Promise<{ replayed: number; resolved: number; failed: number }> {
    const { data } = await api.post<{ replayed: number; resolved: number; failed: number }>(
      `/migration/jobs/${jobId}/dlq/replay-bulk`,
      { filter },
    );
    return data;
  },

  async suppressMigrationDlqEntry(jobId: string, dlqId: string): Promise<void> {
    await api.post(`/migration/jobs/${jobId}/dlq/${dlqId}/suppress`);
  },

  async getMigrationVerificationReport(jobId: string): Promise<{
    report: import('../types/migration.types').VerificationReport;
    whats_missing?: unknown;
  }> {
    const { data } = await api.get<{
      report: import('../types/migration.types').VerificationReport;
      whats_missing?: unknown;
    }>(`/migration/jobs/${jobId}/verification-report`);
    return data;
  },

  /** PR 22: returns a Blob the caller can hand to a download link. */
  async getMigrationVerificationReportCsv(jobId: string): Promise<Blob> {
    const { data } = await api.get<Blob>(
      `/migration/jobs/${jobId}/verification-report.csv`,
      { responseType: 'blob' },
    );
    return data;
  },

  async getMigrationSourceArchiveView(messageId: string): Promise<unknown> {
    const { data } = await api.get<unknown>(
      `/migration/messages/${messageId}/source-archive-view`,
    );
    return data;
  },

  // ─── PR 20: Resumable browser uploads ────────────────────────────────

  async createMigrationUploadSession(input: {
    filename: string;
    total_size: number;
    kind: 'msg' | 'eml' | 'pst' | 'ost' | 'mbox' | 'zip';
    content_type?: string;
    sha256_expected?: string;
    chunk_size?: number;
  }): Promise<import('../types/migration.types').UploadSession> {
    const { data } = await api.post<{ session: import('../types/migration.types').UploadSession }>(
      `/migration/upload-session`,
      input,
    );
    return data.session;
  },

  async appendMigrationUploadChunk(
    sessionId: string,
    chunkIndex: number,
    chunk: Blob | ArrayBuffer,
  ): Promise<import('../types/migration.types').UploadSession> {
    const { data } = await api.put<{ session: import('../types/migration.types').UploadSession }>(
      `/migration/upload-session/${sessionId}/chunks/${chunkIndex}`,
      chunk,
      { headers: { 'Content-Type': 'application/octet-stream' } },
    );
    return data.session;
  },

  async getMigrationUploadSession(sessionId: string): Promise<import('../types/migration.types').UploadSession> {
    const { data } = await api.get<{ session: import('../types/migration.types').UploadSession }>(
      `/migration/upload-session/${sessionId}`,
    );
    return data.session;
  },

  async finalizeMigrationUploadSession(sessionId: string): Promise<import('../types/migration.types').UploadSession> {
    const { data } = await api.post<{ session: import('../types/migration.types').UploadSession }>(
      `/migration/upload-session/${sessionId}/finalize`,
    );
    return data.session;
  },

  async abortMigrationUploadSession(sessionId: string): Promise<void> {
    await api.delete(`/migration/upload-session/${sessionId}`);
  },

  // ─── PR 24: IMAP credential staging ──────────────────────────────────

  async createMigrationCredential(input: {
    kind: 'imap_password';
    host: string;
    port: number;
    use_tls: boolean;
    username: string;
    secret: string;
    reject_unauthorized?: boolean;
    skip_connection_test?: boolean;
  }): Promise<{
    credential_id: string;
    expires_at: string;
    connection_test: { ok: true; folder_count: number; host: string; port: number } | null;
  }> {
    const { data } = await api.post<{
      credential_id: string;
      expires_at: string;
      connection_test: { ok: true; folder_count: number; host: string; port: number } | null;
    }>(`/migration/credentials`, input);
    return data;
  },

  async listMigrationCredentialFolders(
    credentialId: string,
    input?: { reject_unauthorized?: boolean },
  ): Promise<import('../types/migration.types').ImapSourceFolder[]> {
    const { data } = await api.post<{ folders: import('../types/migration.types').ImapSourceFolder[] }>(
      `/migration/credentials/${credentialId}/folders`,
      input ?? {},
    );
    return data.folders;
  },

  async listMigrationCredentialFolderMessages(
    credentialId: string,
    input: {
      folder: string;
      reject_unauthorized?: boolean;
      limit?: number;
      before_uid?: number | null;
      search?: string | null;
    },
  ): Promise<{
    folder: { path: string; raw_path: string; delimiter: string };
    messages: import('../types/migration.types').ImapSourceMessage[];
  }> {
    const { data } = await api.post<{
      folder: { path: string; raw_path: string; delimiter: string };
      messages: import('../types/migration.types').ImapSourceMessage[];
    }>(`/migration/credentials/${credentialId}/folders/messages`, input);
    return data;
  },

  async deleteMigrationCredential(credentialId: string): Promise<void> {
    await api.delete(`/migration/credentials/${credentialId}`);
  },
};

export interface BlockedSender {
  blocked_sender_id: string;
  tenant_id: string;
  mailbox_id: string | null;
  sender_address: string | null;
  sender_domain: string | null;
  reason: 'spam' | 'phishing' | 'abuse' | 'manual';
  source: 'user_report' | 'admin' | 'auto';
  created_by: string | null;
  created_at: string;
}

export interface CreateBlockedSenderInput {
  mailbox_id?: string;
  sender_address?: string;
  sender_domain?: string;
  reason?: 'spam' | 'phishing' | 'abuse' | 'manual';
}

export type RejectedMailAction =
  | 'reject'
  | 'soft reject'
  | 'greylist'
  | 'add header'
  | 'rewrite subject';

export interface RejectedMailSymbol {
  name: string;
  score: number;
  description?: string;
}

export interface RejectedMailRow {
  rejectionId: string;
  unixTime: number;
  messageId: string;
  action: RejectedMailAction;
  score: number;
  requiredScore: number | null;
  sender: string;
  senderHeader: string | null;
  recipient: string;
  recipientHeader: string | null;
  subject: string;
  ip: string | null;
  size: number | null;
  topSymbols: RejectedMailSymbol[];
  releasedAt: string | null;
  releasedToMessageId: string | null;
}

export interface RejectedMailDetail extends RejectedMailRow {
  allSymbols: RejectedMailSymbol[];
  isSkipped: boolean;
  user: string | null;
  rejectionId: string;
  releasedAt: string | null;
  releasedBy: string | null;
  releasedToMessageId: string | null;
}

export interface ListRejectedMailParams {
  actions?: RejectedMailAction[];
  sender?: string;
  recipient?: string;
  q?: string;
  since?: number;
  until?: number;
  limit?: number;
}

export interface ReleaseRejectionResult {
  rejectionId: string;
  released: boolean;
  messageId?: string;
  reason?: string;
  /** Set client-side to disambiguate 200-released vs 409-not-eligible. */
  statusHttp?: number;
}

export interface AllowedSender {
  allowed_sender_id: string;
  tenant_id: string;
  mailbox_id: string;
  sender_address: string | null;
  sender_domain: string | null;
  source: 'admin' | 'auto';
  created_by: string | null;
  created_at: string;
}

export interface CreateAllowedSenderInput {
  mailbox_id: string;
  sender_address?: string;
  sender_domain?: string;
}

export default EdgeMailAdminAPI;
